import { ParsedUrlQuery } from "querystring"
import {
  ALLOWED_LINK_PREVIEW_BOTS_KNOWN_TO_CF,
  ALLOWED_LINK_PREVIEW_BOTS_UNKNOWN_TO_CF,
  ALLOWED_SEO_BOTS,
} from "@/proxy-utils"
import { RegisterGateTypes } from "@/types/register-gate"
import * as amplitude from "@amplitude/analytics-browser"
import { getSessionId } from "@amplitude/analytics-browser"
import type { EnrichmentPlugin, Event } from "@amplitude/analytics-core"
import type { ExperimentClient } from "@amplitude/experiment-js-client"
import isEqual from "lodash/isEqual"

import {
  AB_COHORT_PARAM,
  parseAbCohort,
  resolveCohortForSession,
} from "@/lib/analytics/ab-cohort"
import type { StoredCohort } from "@/lib/analytics/ab-cohort"
import { removeAuthParam } from "@/lib/auth-utils"
import { isStorageAvailable } from "@/lib/auth/browser-capabilities"
import { buildClientAxios } from "@/lib/client-axios"
import { env } from "@/lib/env"
import { mtLocation } from "@/lib/mt-location"
import { present } from "@/lib/utils/presence"
import { canonicalizePath, singleParam } from "@/lib/utils/url"
import { webStorage } from "@/lib/web-storage"
import { nowMillis } from "../date-utils"
import { RELEASE_VERSION } from "../version"
import {
  AmplitudeChatProps,
  AmplitudeEmptyValue,
  AmplitudeEvent,
  AmplitudeEventAndProperties,
  AmplitudeEventWithProps,
  AmplitudeTripProps,
} from "./amplitude-types"
import {
  clearGTMChatProperties,
  resetGTMProperties,
  sendGTMEvent,
  sendGTMUserProperties,
} from "./gtm-utils"

// Replaced by RELEASE_VERSION
// const MindTripAmplitudeVersion = "1.0.240508.2"

// Too noisy - suppressed
const SuppressEventList: (AmplitudeEvent | AmplitudeEventWithProps)[] = [
  "LocationSelect",

  // I don't thkink Close event are that interesting.  Suppressing them for now.
  "DialogDestinationClose",
  "DialogAuthClose",
  "DialogForgotPasswordClose",
  "DialogVerifyEmailClose",
  "DialogClose",
]

// Global properties
let prevInitProps: AmplitudeInitBaseProps | undefined
let prevUserProperties: amplitude.Identify | undefined
let experimentClient: ExperimentClient | undefined
let featureFlagsLoaded = false
let isAmplitudeReady = false
let isCrawlerUser = false

// Queue for events fired before Amplitude is initialized
let pendingEvents: Array<{
  eventArgs: AmplitudeEvent | AmplitudeEventAndProperties
  properties: Record<string, unknown>
}> = []

// Amplitude
export const amplitudeEnabled = () => {
  return !!env("NEXT_PUBLIC_AMPLITUDE_API_KEY")
}

export type AmplitudeInitBaseProps = {
  type: string
}

export interface AmplitudeInitProps extends AmplitudeInitBaseProps {
  type: "initProps"
  uuid?: string
  provider?: string
  handle?: string
  status: "authenticated" | "unauthenticated"
  rateLimit?: AmplitudeRateLimit
  orgid?: string
  permissions?: string[]
  has_thatch_data?: boolean
  is_from_thatch?: boolean
  is_thatch_creator?: boolean
  thatch_redirect?: boolean
  thatch_referrer?: string
  is_crawler?: boolean
  referer?: string
  mt_referer?: string
  session_tracking?: boolean
}

export interface AmplitudeInitChatProps extends AmplitudeInitBaseProps {
  type: "chatProps"
  chat: AmplitudeChatProps
  trip?: AmplitudeTripProps
}

export interface AmplitudeInitRateLimitProps extends AmplitudeInitBaseProps {
  type: "rateLimitProps"
  rateLimit?: AmplitudeRateLimit
}

export interface AmplitudeInitUtmProps extends AmplitudeInitBaseProps {
  type: "utmProps"
  utmSource?: string
  utmCampaign?: string
  utmMedium?: string
  utmContent?: string
  utmTerm?: string
  utmPartner?: string
  epik?: string
}

export interface AmplitudeInitRegisterGateProps extends AmplitudeInitBaseProps {
  type: "registerGateProps"
  where: RegisterGateTypes
}

export interface AmplitudeRateLimit {
  reason: string
}

const verticalFromPath = (pathname: string): string | undefined => {
  switch (pathname) {
    case "/flights":
      return "flights"
    case "/stays":
      return "stays"
    default:
      return undefined
  }
}

const enrichPageUrlPlugin = (): EnrichmentPlugin => {
  return {
    execute: async (event: Event) => {
      const vertical = verticalFromPath(mtLocation.pathname)
      event.event_properties = {
        ...event.event_properties,
        page_url: removeAuthParam(mtLocation.href),
        page_title: document.title,
        canonical_path: canonicalizePath(mtLocation.pathname),
        ...(vertical ? { vertical } : {}),
      }
      return event
    },
  }
}

// Decodes the `abc` email-link param once on init and stashes the cohort for the browser
// session. The Amplitude sessionId is bound later by the enrichment plugin, NOT here:
// amplitude.init() finishes its session setup asynchronously, so getSessionId() can still be
// undefined at this point.
const captureAbCohort = () => {
  const token = new URLSearchParams(mtLocation.search).get(AB_COHORT_PARAM)
  if (!token) return

  const cohort = parseAbCohort(token)
  if (!cohort) return

  webStorage.setJSON("mindtrip.abTestCohort", cohort, { type: "session" })
}

// Stamps ab_test / ab_cohort onto every event for the session that landed from a tagged email
// link. Binds to the first real Amplitude sessionId it observes (init sets it async, so the
// cohort is captured before one exists), then drops the cohort once a new Amplitude session
// begins. sessionStorage additionally bounds it to the browser session.
const enrichAbCohortPlugin = (): EnrichmentPlugin => {
  return {
    execute: async (event: Event) => {
      const stored = webStorage.tryGetJSON<StoredCohort>(
        "mindtrip.abTestCohort",
        {
          type: "session",
        }
      )
      const { label, store, drop } = resolveCohortForSession(
        stored,
        getSessionId()
      )

      if (drop) {
        webStorage.removeItem("mindtrip.abTestCohort", { type: "session" })
      }
      if (store) {
        webStorage.setJSON("mindtrip.abTestCohort", store, { type: "session" })
      }
      if (label) {
        event.event_properties = { ...event.event_properties, ...label }
      }
      return event
    },
  }
}

export const isAmplitudeInitialized = () => {
  return isAmplitudeReady
}

export const initAmplitude = function (initProps: AmplitudeInitProps) {
  if (!amplitudeEnabled() || isAmplitudeInitialized()) {
    return false
  }

  const amplitudeApiKey = env("NEXT_PUBLIC_AMPLITUDE_API_KEY")
  const amplitudeEndPoint = env("NEXT_PUBLIC_AMPLITUDE_END_POINTS")
  const amplitudeServerZone = env("NEXT_PUBLIC_AMPLITUDE_SERVER_ZONE")
  if (
    amplitudeApiKey &&
    (prevInitProps == null || !isEqual(prevInitProps, initProps))
  ) {
    if (initProps.is_crawler) {
      console.debug("Amp disabled for crawler", initProps.uuid, initProps.orgid)
      isCrawlerUser = true
      return false
    }

    let options = {}
    options = {
      ...options,
      appVersion: RELEASE_VERSION || AmplitudeEmptyValue,
      // flushQueueSize: 1,
    }
    const defaultTracking = {
      attribution: {
        initialEmptyValue: AmplitudeEmptyValue,
        includeUtm: true,
        includeReferrer: true,
        includeGclid: true,
      },
      pageViews: false,
      sessions: initProps.session_tracking ?? false, // Session events add to expense so only do for DMOs
      formInteractions: false,
      fileDownloads: false,
    }
    options = {
      ...options,
      defaultTracking,
      // Amplitude's experimental page URL enrichment plugin reads the
      // sessionStorage property before its own availability check. Supplying
      // autocapture options disables only that plugin when the property is
      // inaccessible while preserving our existing tracking configuration.
      ...(isStorageAvailable("session")
        ? {}
        : {
            autocapture: {
              ...defaultTracking,
              pageUrlEnrichment: false,
            },
          }),
      automaticSessionTracking: true,
      // The SDK otherwise fetches sr-client-cfg.amplitude.com during init (and waits for
      // it) to pick up dashboard-side autocapture and diagnostics settings we don't use.
      fetchRemoteConfig: false,
      // logLevel: amplitude.Types.LogLevel.Debug,
    }
    if (amplitudeEndPoint) {
      options = { ...options, serverUrl: amplitudeEndPoint }
    }
    if (amplitudeServerZone) {
      options = { ...options, serverZone: amplitudeServerZone }
    }

    try {
      amplitude.init(amplitudeApiKey, undefined, options)
      // Need to give it a name other we get warning from Amplitude
      // Plugin name is undefined.
      // Generating a random UUID for plugin name: fe1a3419-6bd9-4312-a8c5-0165e419a7f6.
      const plugIn = enrichPageUrlPlugin()
      plugIn.name = "MT_EnrichPageUrlPlugIn"
      amplitude.add(plugIn)

      const cohortPlugIn = enrichAbCohortPlugin()
      cohortPlugIn.name = "MT_EnrichAbCohortPlugIn"
      amplitude.add(cohortPlugIn)
      captureAbCohort()

      if (initProps.uuid) {
        setAmplitudeUserId(initProps.uuid)
      }
      setAnalyticsUserProperties(initProps)

      prevInitProps = initProps

      // This fails CORS so we need a CNAME for api-lab.mindtrip.ai to api.lab.amplitude.com.
      // Re-enable: import { Experiment } from "@amplitude/experiment-js-client" and getDeviceId above.
      // const experimentServerUrl = amplitudeServerZone === 'EU' ? 'https://api.lab.eu.amplitude.com' : undefined
      // experimentClient ||= Experiment.initialize(amplitudeApiKey, { serverUrl: experimentServerUrl })
      // For anon user, we will only have deviceId as we don't have uuid at that point
      // const deviceId = getDeviceId()
      // const userContext = {
      //   user_id: initProps.uuid, // May have uuid or not (anon user)
      //   device_id: deviceId, // We should have device id at least
      // }
      // void fetchFeatureFlags(userContext)
      featureFlagsLoaded = true

      isAmplitudeReady = true

      // Flush any events that were queued before init
      flushPendingEvents()

      console.debug("Amp initialized")
    } catch (e) {
      console.log("Error in initializing Amplitude", e)
    }

    return true
  }
  return false
}

export const setAmplitudeUserDevice = (installationToken: string) => {
  if (amplitudeEnabled()) {
    amplitude.setDeviceId(installationToken)
  }
}

export const setAmplitudeUserId = (userId: string) => {
  if (amplitudeEnabled()) {
    amplitude.setUserId(userId)
  }
}

export const setAnalyticsUserProperties = (
  properties:
    | AmplitudeInitProps
    | AmplitudeInitChatProps
    | AmplitudeInitRateLimitProps
    | AmplitudeInitUtmProps
    | AmplitudeInitRegisterGateProps
) => {
  // GTM
  sendGTMUserProperties(properties)

  let identifyEvent
  if (amplitudeEnabled()) {
    if (properties.type === "initProps") {
      identifyEvent = new amplitude.Identify()
      if (properties.uuid) {
        identifyEvent.set("uuid", properties.uuid)
      }
      if (properties.provider) {
        identifyEvent.set("provider", properties.provider)
      }
      if (properties.handle) {
        identifyEvent.set("handle", properties.handle)
      }
      if (properties.status) {
        identifyEvent.set("status", properties.status)
      }
      if (properties.orgid) {
        identifyEvent.set("orgid", properties.orgid)
      }
      if (properties.rateLimit?.reason) {
        identifyEvent.set("rateLimit.reason", properties.rateLimit.reason)
      }
      if (properties.permissions) {
        identifyEvent.set("permissions", properties.permissions)
      }
      if (typeof properties.has_thatch_data === "boolean") {
        identifyEvent.set("has_thatch_data", properties.has_thatch_data)
      }
      if (typeof properties.is_from_thatch === "boolean") {
        identifyEvent.set("is_from_thatch", properties.is_from_thatch)
      }
      if (typeof properties.is_thatch_creator === "boolean") {
        identifyEvent.set("is_thatch_creator", properties.is_thatch_creator)
      }
      if (typeof properties.thatch_redirect === "boolean") {
        identifyEvent.set("thatch_redirect", properties.thatch_redirect)
      }
      if (properties.thatch_referrer) {
        identifyEvent.set("thatch_referrer", properties.thatch_referrer)
      }
      if (properties.mt_referer) {
        identifyEvent.set("mt_referer", properties.mt_referer)
      }
      if (present(properties.referer)) {
        const mindtripRegex =
          /^https?:\/\/(?:(?:[a-z0-9-]+\.)?mindtrip\.ai|localhost(?::\d+)?)(?:\/|$)/i
        if (
          properties.referer !== AmplitudeEmptyValue &&
          !mindtripRegex.test(properties.referer)
        ) {
          // If we came from /create-anon, we will have referer that we stashed away.
          // When create-anon reload/redirect, we lost the original referer.
          // So override what Amplitude has for these referrer.
          identifyEvent.set("referrer", properties.referer)
          identifyEvent.set("initial_referrer", properties.referer)
          const { host } = new URL(properties.referer)
          if (present(host)) {
            identifyEvent.set("referring_domain", host)
            identifyEvent.set("initial_referring_domain", host)
          }
        }
      }
    } else if (properties.type === "chatProps") {
      identifyEvent = new amplitude.Identify()
      if (properties.chat) {
        identifyEvent.set("chat.id", properties.chat.id)
      }
      if (properties.trip && properties.trip.id && properties.trip.name) {
        identifyEvent.set("trip.id", properties.trip.id)
        identifyEvent.set("trip.name", properties.trip.name)
      }
    } else if (properties.type === "rateLimitProps") {
      identifyEvent = new amplitude.Identify()
      if (properties.rateLimit?.reason) {
        identifyEvent.set("rateLimit.reason", properties.rateLimit.reason)
      }
    } else if (properties.type === "utmProps") {
      identifyEvent = new amplitude.Identify()
      if (properties.utmSource) {
        identifyEvent.set("utm_source", properties.utmSource)
      }
      if (properties.utmMedium) {
        identifyEvent.set("utm_medium", properties.utmMedium)
      }
      if (properties.utmCampaign) {
        identifyEvent.set("utm_campaign", properties.utmCampaign)
      }
      if (properties.utmContent) {
        identifyEvent.set("utm_content", properties.utmContent)
      }
      if (properties.utmTerm) {
        identifyEvent.set("utm_term", properties.utmTerm)
      }
      if (properties.utmPartner) {
        identifyEvent.set("utm_partner", properties.utmPartner)
      }
      if (properties.epik) {
        identifyEvent.set("epik", properties.epik)
      }
    } else if (properties.type === "registerGateProps") {
      identifyEvent = new amplitude.Identify()
      if (properties.where) {
        identifyEvent.set("registerGate.where", properties.where)
      }
    }

    if (identifyEvent && !isEqual(prevUserProperties, identifyEvent)) {
      prevUserProperties = identifyEvent
      amplitude.identify(identifyEvent)
    }

    // June 25, 2024 - Prakoon - Test if we can set our own initial utm_*
    // Yes, it works.
    //
    // const identifyEvent = new amplitude.Identify()
    // identifyEvent.set("initial_utm_source", "pk_test_initial_utm_src")
    // identifyEvent.set("initial_utm_medium", "pk_test_initial_utm_medium")
    // identifyEvent.set("initial_utm_campaign", "pk_test_initial_utm_campaign")
    // amplitude.identify(identifyEvent)
  }
}

export const optOutAmplitudeTracking = (newOptOut: boolean) => {
  if (amplitudeEnabled()) {
    if (newOptOut) {
      console.log("User opted out of amplitude")
      // Set user properties and send event before optout
      const identifyEvent = new amplitude.Identify()
      identifyEvent.set("optOut", true)
      amplitude.identify(identifyEvent)
      sendAnalyticsEvent("UserOptOut")

      // Finally optout
      amplitude.setOptOut(newOptOut)
    } else {
      console.log("User opted in to amplitude")
      // First set optIn
      amplitude.setOptOut(newOptOut)

      // Then set the user properties as optin and also send event
      const identifyEvent = new amplitude.Identify()
      identifyEvent.set("optOut", false)
      amplitude.identify(identifyEvent)
      sendAnalyticsEvent("UserOptIn")
    }
  }
}

export const clearAnalyticsAllUserProperties = () => {
  if (amplitudeEnabled()) {
    amplitude.identify(new amplitude.Identify().clearAll())
    prevInitProps = undefined
  }
}

export const clearAnalyticsChatProperties = () => {
  clearGTMChatProperties()

  if (amplitudeEnabled()) {
    const identifyEvent = new amplitude.Identify()
    identifyEvent.unset("chat.id")
    identifyEvent.unset("trip.id")
    identifyEvent.unset("trip.name")
    amplitude.identify(identifyEvent)
  }
}

export const resetAnalyticsSession = () => {
  resetGTMProperties()

  if (amplitudeEnabled()) {
    amplitude.reset()
    // Old way
    // amplitude.setUserId(undefined) // not string 'null'
    // amplitude.setDeviceId(generateUUID())
  }
}

// Flush any events that were queued before Amplitude initialized
const flushPendingEvents = () => {
  if (pendingEvents.length > 0) {
    console.debug(`Flushing ${pendingEvents.length} queued analytics events`)
    const eventsToFlush = [...pendingEvents]
    pendingEvents = []
    eventsToFlush.forEach(({ eventArgs, properties }) => {
      const event = typeof eventArgs === "string" ? eventArgs : eventArgs.name
      amplitude.track(event, properties, {})
    })
  }
}

export const sendAnalyticsEvent = (
  eventArgs: AmplitudeEvent | AmplitudeEventAndProperties
) => {
  const event = typeof eventArgs === "string" ? eventArgs : eventArgs.name
  const eventProperties =
    typeof eventArgs === "string" ? {} : eventArgs.properties

  // For Bot Detection
  // You might want to specifically look at:
  // navigator.webdriver === true → ✅ Likely a bot
  // navigator.userAgent → contains "bot", "headless", "crawl" etc.
  // navigator.platform is unusual (like "Linux x86_64" in a mobile context)
  // hardwareConcurrency is 1 (often headless)
  // deviceMemory is very low
  //
  // 📊 For Analytics & Segmentation
  // You might track:
  // userAgent → Parse for browser, OS, and device type
  // platform, vendor → Browser & OS trends
  // language / languages → Localization, targeting
  // deviceMemory / hardwareConcurrency → Performance segmentation
  // maxTouchPoints → Detect touch-enabled devices
  let properties = { ...eventProperties }
  if (typeof navigator !== "undefined") {
    const browser = {
      userAgent: navigator.userAgent,
      // platform: navigator.platform, // Can get from UserAgent already
      language: navigator.language,
      // languages: navigator.languages, // We already have language
      // deviceMemory: navigator.deviceMemory || 'unknown',
      hardwareConcurrency: navigator.hardwareConcurrency || "unknown",
      maxTouchPoints: navigator.maxTouchPoints,
      // vendor: navigator.vendor, // Deprecated
      webdriver: navigator.webdriver,
    }
    properties = { ...properties, browser }
  }

  // GTM
  sendGTMEvent(eventArgs)

  // Amplitude
  if (amplitudeEnabled()) {
    if (!SuppressEventList.includes(event) && !isCrawlerUser) {
      if (isAmplitudeInitialized()) {
        return amplitude.track(event, properties, {})
      } else {
        // Queue the event to be sent after Amplitude initializes
        console.debug(`Queuing analytics event until init: ${event}`)
        pendingEvents.push({ eventArgs, properties })
      }
    } else {
      console.debug("Amplitude event suppressed for: ", event, properties)
    }
  }
  return undefined // Indicate event not fired
}

// Jan 16, 2026
// I don't think anyone is using these revenues.  We sent out but no one is looking at this really in Amplitude

// Experimental state: try out the revenue of Amplitude
// export const sendAmplitudeHotelRevenue = (
//   hotelBooking: HotelBooking,
//   cancel = false
// ) => {
//   if (amplitudeEnabled()) {
//     const roomTitle =
//       hotelBooking.available_room?.title || hotelBooking.vendor_data?.title
//     const productId = `Hotel: ${hotelBooking.entity_ref}: ${hotelBooking.entity_name}: ${roomTitle}`
//     const priceDetails =
//       hotelBooking.available_room?.price_details ||
//       hotelBooking.vendor_data?.price_details
//     const price = priceDetails.display_sub_total * (cancel ? -1 : 1)
//     const tax = priceDetails.display_taxes * (cancel ? -1 : 1)
//     const num_nights = hotelBooking.properties.booking.num_nights || 1
//     const num_rooms = hotelBooking.properties.booking.num_rooms || 1
//     let hotelRoomProps = {}
//     if (hotelBooking.ref) {
//       hotelRoomProps = { ...hotelRoomProps, bookingRef: hotelBooking.ref }
//     }
//     if (hotelBooking.entity_ref) {
//       hotelRoomProps = { ...hotelRoomProps, entityRef: hotelBooking.entity_ref }
//     }
//     if (hotelBooking.entity_name) {
//       hotelRoomProps = { ...hotelRoomProps, name: hotelBooking.entity_name }
//     }
//     hotelRoomProps = {
//       ...hotelRoomProps,
//       room: roomTitle,
//       numNights: num_nights,
//       numRooms: num_rooms,
//     }
//     const roomRevenueEvent = new amplitude.Revenue()
//       .setProductId(productId)
//       .setPrice(price)
//       .setQuantity(1)
//       .setRevenueType("hotel")
//       .setEventProperties(hotelRoomProps)
//     amplitude.revenue(roomRevenueEvent)

//     const roomTaxEvent = new amplitude.Revenue()
//       .setProductId(productId)
//       .setPrice(tax)
//       .setQuantity(1)
//       .setRevenueType("hotel_tax")
//       .setEventProperties(hotelRoomProps)
//     amplitude.revenue(roomTaxEvent)
//   }
// }

// export const sendAmplitudeFlightRevenue = (
//   flightBooking: FlightBookingApi,
//   cancel = false
// ) => {
//   if (amplitudeEnabled()) {
//     let flightBookingProps = {}
//     if (flightBooking.id) {
//       flightBookingProps = {
//         ...flightBookingProps,
//         bookingId: flightBooking.id,
//       }
//     }
//     flightBookingProps = {
//       ...flightBookingProps,
//       status: flightBooking.status,
//       flight: flightItineraryApiToAmplitudeProperties(
//         flightBooking.flight_itinerary
//       ),
//     }

//     const productId = `Flight: ${flightBooking.id}`
//     const price =
//       (flightBooking.flight_itinerary.properties.price.total -
//         flightBooking.flight_itinerary.properties.price.taxes_and_fees) *
//       (cancel ? -1 : 1)
//     const taxAndFees =
//       flightBooking.flight_itinerary.properties.price.taxes_and_fees *
//       (cancel ? -1 : 1)

//     const flightRevenueEvent = new amplitude.Revenue()
//       .setProductId(productId)
//       .setPrice(price)
//       .setQuantity(1)
//       .setRevenueType("flight")
//       .setEventProperties(flightBookingProps)
//     amplitude.revenue(flightRevenueEvent)

//     const flightTaxFeesEvent = new amplitude.Revenue()
//       .setProductId(productId)
//       .setPrice(taxAndFees)
//       .setQuantity(1)
//       .setRevenueType("flight_tax_fees")
//       .setEventProperties(flightBookingProps)
//     amplitude.revenue(flightTaxFeesEvent)
//   }
// }

export type UtmSource = ParsedUrlQuery | URLSearchParams

export const getUtmParams = (query: UtmSource): AmplitudeInitUtmProps => {
  const utmProperties: AmplitudeInitUtmProps = { type: "utmProps" }

  // Helper function to get value regardless of input type
  const getValue = (key: string): string | undefined => {
    if (query instanceof URLSearchParams) {
      const value = query.get(key)
      return value || undefined
    }
    return singleParam(query[key])
  }

  const utmFields = {
    pid: "utmPartner",
    utm_source: "utmSource",
    utm_campaign: "utmCampaign",
    utm_medium: "utmMedium",
    utm_content: "utmContent",
    utm_term: "utmTerm",
  } as const

  Object.entries(utmFields).forEach(([queryKey, propKey]) => {
    const value = getValue(queryKey)
    if (value) {
      utmProperties[propKey] = value
    }
  })

  // Pinterest click id
  if (getValue("epik")) {
    utmProperties.epik = getValue("epik")
  }

  return utmProperties
}

export const getUtmParamString = (properties: AmplitudeInitUtmProps) => {
  const strList = []
  if (properties.utmSource) {
    strList.push(`utm_source=${properties.utmSource}`)
  }
  if (properties.utmMedium) {
    strList.push(`utm_medium=${properties.utmMedium}`)
  }
  if (properties.utmCampaign) {
    strList.push(`utm_campaign=${properties.utmCampaign}`)
  }
  if (properties.utmContent) {
    strList.push(`utm_content=${properties.utmContent}`)
  }
  if (properties.utmTerm) {
    strList.push(`utm_term=${properties.utmTerm}`)
  }
  if (properties.utmPartner) {
    strList.push(`utm_partner=${properties.utmPartner}`)
  }
  return present(strList) ? strList.join("&") : undefined
}

export const fetchFeatureFlags = async (userContext = {}) => {
  if (!experimentClient) {
    featureFlagsLoaded = true
    return
  }
  try {
    await experimentClient.fetch(userContext)
    featureFlagsLoaded = true
  } catch (error) {
    console.error("Error fetching feature flags:", error)
    featureFlagsLoaded = true
  }
}

export const waitForFeatureFlags = async (): Promise<void> => {
  if (featureFlagsLoaded) return
  await new Promise<void>((resolve) => {
    const start = nowMillis()
    const id = setInterval(() => {
      if (featureFlagsLoaded || nowMillis() - start > 1500) {
        clearInterval(id)
        resolve()
      }
    }, 50)
  })
}

export const getFeatureFlag = (featureFlag: string, fallback: string) => {
  if (!featureFlagsLoaded) return undefined
  if (!experimentClient) return fallback

  try {
    const value = experimentClient.variant(featureFlag).value
    return value || fallback
  } catch (error) {
    console.error(`Error getting feature flag ${featureFlag}:`, error)
    return fallback
  }
}

/*
export const getPlaceDetailTests = (options?: {
  hasBookButton?: boolean
}): AmplitudeTest[] | undefined => {
  if (!featureFlagsLoaded) return undefined
  const tests: AmplitudeTest[] = []

  if (options?.hasBookButton) {
    const bookButtonLabelVariant = getFeatureFlag(
      "book_button_label",
      "control"
    )
    if (bookButtonLabelVariant) {
      tests.push({
        name: "book_button_label",
        value: bookButtonLabelVariant,
      })
    }
  }

  return tests.length > 0 ? tests : undefined
}
*/

// From: https://help.amplitude.com/hc/en-us/articles/206404628#h_533ee533-f04d-49d3-873f-5836945f43a6
// Amplitude can hook up the anonymous session data to the user that will login.
// So we don't have to do some of the trick like in GA with dimension of scope = session
//
// No need the following codes of us setting the cookie for the anonymous user
//
// const MINDTRIP_UUID_COOKIE_NAME = "mt-uuid"

// export const createOrGetUUID = (uuid?: string) => {
//   // Do we have in the cookie?
//   if (!uuid) {
//     uuid = getCookie(MINDTRIP_UUID_COOKIE_NAME)
//   }
//   // If we don't have in cookie, then generate one and save into cookie
//   if (!uuid) {
//     uuid = generateUUID()
//   }
//   setCookie(MINDTRIP_UUID_COOKIE_NAME, uuid)
//   return uuid
// }

// export const clearCookieUUID = () => {
//   clearCookie(MINDTRIP_UUID_COOKIE_NAME)
// }

// const generateUUID = () => {
//   return crypto.randomUUID()
// }

// const getCookie = (name: string) => {
//   const value = `; ${document.cookie}`
//   const parts = value.split(`; ${name}=`)
//   if (parts && parts.length === 2) {
//     return parts.pop()?.split(";").shift()
//   }
// }

// const setCookie = (name: string, uuid: string, expire?: string) => {
//   // Set cookies that will expire long time from now (well, the browser sets it to be like a year+ from now)
//   if (!expire) {
//     expire = "Fri, 31 Dec 9999 23:59:59 GMT"
//   }
//   document.cookie = `${name}=${uuid}; expires=${expire}; path=/`
// }

// const clearCookie = (name: string) => {
//   document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
// }

/**
 * Updates the session props cookie with the Amplitude session ID
 * This helps link backend analytics with frontend Amplitude events
 */
export const updateAmplitudeSessionId = async (
  amplitudeSessionId?: number,
  uuid?: string
): Promise<void> => {
  const sessionId =
    amplitudeSessionId !== undefined ? amplitudeSessionId : getSessionId()

  if (sessionId === undefined) {
    return
  }

  // Check if we're in a browser environment
  if (typeof window !== "undefined") {
    const userAgent = window.navigator?.userAgent || ""

    // Skip for crawler bots
    if (
      ALLOWED_SEO_BOTS.test(userAgent) ||
      ALLOWED_LINK_PREVIEW_BOTS_KNOWN_TO_CF.test(userAgent) ||
      ALLOWED_LINK_PREVIEW_BOTS_UNKNOWN_TO_CF.test(userAgent)
    ) {
      return
    }

    // Prevent duplicate API calls for the same session ID
    const lastSentSessionId = webStorage.getItem("lastSentAmplitudeSessionId")
    if (lastSentSessionId && parseInt(lastSentSessionId) === sessionId) {
      return
    }

    webStorage.setItem("lastSentAmplitudeSessionId", sessionId.toString())
  }

  try {
    const nextAxios = buildClientAxios("nextjs")
    await nextAxios.post("/api/update-session-props", {
      amplitudeSessionId,
      uuid,
    })
  } catch (e) {
    return
  }
}
